import {
	Button,
	cn,
	deliveryMethodIcons,
	LogoPay,
} from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { Price } from "~/components/price.component";
import { PriceComparisonDiscountBadge } from "~/features/price-comparison/price-comparison-discount-badge.component";
import type { PriceComparisonDiscount } from "~/features/price-comparison/price-comparison-discounts";
import { PriceComparisonOldPrice } from "~/features/price-comparison/price-comparison-old-price.component";
import { isCombinedPaymentEnabled } from "~/features/products/combined-payment.utils";
import { ProductDetailsVendorItemName } from "~/features/products/product-details-vendor-item-name.component";
import {
	createOfferCardDeliveryBadges,
	type ProductDetailsSelectVendorItem,
} from "~/features/products/product-details-vendor-select.utils";
import type { OfferCardDeliveryKind } from "~/features/products/product-details-vendor-select-trigger.component";

const productDetailsVendorSelectItemVariant = cva(
	"w-full flex flex-col items-start gap-1 bg-white py-3 px-4 rounded-xl hover:bg-gray-200 cursor-pointer text-start [&>span]:w-full",
	{
		variants: {
			variant: {
				selected: "border-[2px] border-ansay-main outline-ansay-main",
				normal: "border-[2px] border-transparent outline-ansay-main",
			},
		},
		defaultVariants: {
			variant: "normal",
		},
	},
);

const getDeliveryIcon = (kind: OfferCardDeliveryKind) =>
	deliveryMethodIcons[kind];

export function ProductDetailsVendorItem({
	title,
	address,
	deliveryMethods,
	deliveryMethodsData,
	showDeliveryMethodBadgesThreshold,
	priceComparisonDiscount,
	price,
	product,
	variant,
	featured,
	combinedPaymentEnabled,
	onSelection,
}: ProductDetailsSelectVendorItem & {
	onSelection?: () => void;
	priceComparisonDiscount?: PriceComparisonDiscount;
	product: ProductNoEdibleInfo;
} & VariantProps<typeof productDetailsVendorSelectItemVariant>) {
	const { t, i18n } = useTranslation(["common"]);
	const badges = createOfferCardDeliveryBadges({
		deliveryMethodsData,
		locale: i18n.language,
		formatFreeFromLabel: (amount) => t("common:delivery.freeFrom", { amount }),
	});
	const showBadges =
		deliveryMethods.length >= showDeliveryMethodBadgesThreshold &&
		badges.length > 0;
	const showCombinedPaymentBadge = isCombinedPaymentEnabled({
		combinedPaymentEnabled,
	});
	const showDiscount = priceComparisonDiscount?.visible === true;

	return (
		<Button
			variant="ghost"
			className={productDetailsVendorSelectItemVariant({ variant })}
			onClick={onSelection}
		>
			<div className="flex w-full flex-col gap-1 min-w-0">
				<div className="flex flex-row justify-between w-full gap-1">
					<ProductDetailsVendorItemName
						title={title}
						variant={variant}
						featured={featured}
					/>
					<Price
						variant="raw"
						className="self-center text-end text-body-bold"
						price={Number(price)}
						category={product.category}
						kind={product.kind}
					/>
				</div>

				<div className="flex w-full flex-col gap-2 min-w-0">
					<div className="flex w-full items-start justify-between gap-3">
						{address ? (
							<span
								className="min-w-0 flex-1 self-center text-body-sm text-gray-500 truncate"
								title={address}
							>
								{address}
							</span>
						) : (
							<span className="min-w-0 flex-1" />
						)}

						<div className="flex shrink-0 items-center justify-end gap-1">
							<PriceComparisonOldPrice
								productCategory={product.category}
								productKind={product.kind}
								priceComparisonDiscount={priceComparisonDiscount}
							/>
							<PriceComparisonDiscountBadge
								discountPercentage={priceComparisonDiscount?.discountPercentage}
							/>
							{showCombinedPaymentBadge && (
								<LogoPay
									title="DrAnsay Pay"
									className={cn(
										"shrink-0 h-5 w-9 md:h-6 md:w-[43.2px]",
										showDiscount && "ml-1",
									)}
									data-testid="product-details-vendor-item-combined-payment-badge"
								/>
							)}
						</div>
					</div>

					{showBadges && (
						<div className="flex flex-wrap items-center gap-1">
							{badges.map((badge) => (
								<div
									key={badge.kind}
									className={clsx(
										"inline-flex items-center gap-1 rounded bg-ansay-background text-caption text-ansay-700 h-7",
										badge.label ? "px-2" : "w-7 justify-center",
									)}
								>
									{getDeliveryIcon(badge.kind)}
									{badge.label && <span>{badge.label}</span>}
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</Button>
	);
}
